<template>
  <Main>
    <template v-slot:toolbar>
      <div class="white">
        <StepToolbar :steps="toolbarSteps" :current-step="currentStep" />
      </div>
    </template>
    <template v-slot:breadcrumb>
      <BaseBreadcrumb
        @return="previousPage"
        :items="breadcrumb"
        class="d-none d-sm-flex"
      />
    </template>
    <template v-slot:breadcrumb-mobile>
      <BaseBreadcrumb @return="previousPage" :items="breadcrumb" />
    </template>
    <v-form ref="form" @submit.prevent="submit">
      <NewEconomicGroupForm
        v-if="step === 'group'"
        :value="groupInfo"
        :balance-documents-model="balanceDocuments"
        :balance-error-messages="balanceFormErrors"
        :documents-model="otherDocuments"
        :advisor-error-messages="advisorErrorMessages"
        :atendentList="atendentList"
        :comercialList="comercialList"
        :productList="productList"
        :contacts-error-messages="contactsErrorMessages"
        :office-list="officeList"
        @more-advisories="onRequestedMoreAdvisories"
        @open-balance-modal="onToggleBalanceModal"
        @open-single-document-modal="onToggleSingleDocumentModal"
        @preview-file="onPreviewFile"
        @remove-file="onRemoveFile"
        @remove-others-documents="onRemoveEconomicGroupOthersDocuments"
        @preview-document="onPreviewDocument"
        @remove-document="onRemoveDocument"
        @open-contact-modal="isContactModalOpen = true"
        @edit-contact="onEditContact"
        @search-users="onSearchUser"
      >
        <template v-slot:header>
          <h3 class="gray--text mb-8" v-if="userAreaIsGarantia">
            GRUPO ECONÔMICO
          </h3>
          <div class="d-flex mb-4">
            <v-switch
              v-model="groupInfo.exibir_comissao_cotacao"
              class="pa-0 ma-0"
              color="secondary"
              inset
              hide-details
              dense
            />
            <p class="darkBluishGray--text caption mb-0">
              Exibir Comissão na Cotação?
            </p>
          </div>
        </template>
      </NewEconomicGroupForm>
      <NewCompanyForm
        v-else
        v-model="companyInfo"
        :companySectionTitle="textMatriz"
        :balance-documents-model="companyBalanceDocuments"
        :balance-error-messages="[]"
        :documents-model="companyOtherDocuments"
        @open-balance-modal="onToggleBalanceModal"
        @open-single-document-modal="onToggleSingleDocumentModal"
        @preview-document="onPreviewDocument"
        @remove-document="onRemoveDocument"
        @remove-others-documents="onRemoveCompanyOthersDocuments"
      >
        <h3 class="gray--text mb-2">GRUPO ECONÔMICO: {{ groupInfo.nome }}</h3>
        <FormSection class="pt-9" :title="textMatrizFilial">
          <CompanyDetailsFormSection v-model="companyInfo" />
        </FormSection>
      </NewCompanyForm>
      <BaseFooter>
        <div class="d-flex justify-end">
          <BaseButton
            class="error text-uppercase subtitle-2 font-weight-bold"
            min-width="200"
            :to="{ name: 'economic_group' }"
          >
            Cancelar
          </BaseButton>
          <BaseButton
            type="submit"
            class="secondary text-uppercase subtitle-2 font-weight-bold ml-5"
            min-width="200"
          >
            PRÓXIMO
          </BaseButton>
        </div>
      </BaseFooter>
    </v-form>
    <BalanceModal
      v-if="isBalanceModalOpen"
      v-model="isBalanceModalOpen"
      :form-model="selectBalanceDocumentModel"
      @submit="onCompletedBalanceDocumentSelection"
      @close="onToggleBalanceModal"
    />
    <SingleDocumentModal
      v-if="isDocumentModalOpen"
      v-model="isDocumentModalOpen"
      :title="documentModalTitle"
      :type-document="documentModalForKey"
      @submit="onCompletedDocumentSelection"
      @close="onToggleDocumentModal"
    />
    <NewContactModal
      v-model="isContactModalOpen"
      :item="contact"
      title="INCLUIR NOVO CONTATO"
      @submit="onAddContact"
      @close="isContactModalOpen = false"
    />
    <ModalNewEconomicGroup
      v-model="modalNewItem"
      @close="closeModalNewItem"
      @new="newTaker"
      :textNotification="textNotification"
    />
    <ModalCancelEconomicGroup
      @close="closeModalCancel"
      v-model="modalCancel"
      title="SAIR SEM SALVAR"
      text="As alterações não foram salvas. Deseja realmente sair da tela?"
    />
  </Main>
</template>

<script>
import _ from "lodash";
import * as BRUtils from "@brazilian-utils/brazilian-utils";
import { defineGuid, objectGuid } from "@/utils/guid";
import { normalizeCnpj } from "@/utils/documents.js";
import { mapActions, mapState } from "vuex";
import { LoadingRequest } from "@/mixins/LoadingMixin";
import EconomicGroupMixin from "../mixins/EconomicGroupMixin";
import CompanyMixin from "@/mixins/CompanyMixin";
import StepToolbar from "@/layouts/StepToolbar.vue";
import NewEconomicGroupForm from "../components/NewEconomicGroupForm.vue";
import NewCompanyForm from "../components/NewCompanyForm.vue";
import BalanceModal from "../components/BalanceModal.vue";
import SingleDocumentModal from "../components/SingleDocumentModal.vue";
import NewContactModal from "../components/NewContactModal.vue";
import CompanyDetailsFormSection from "@/components/form/CompanyDetailsFormSection.vue";
import FormSection from "@/components/FormSection.vue";
import ModalNewEconomicGroup from "../components/ModalNewEconomicGroup";
import ModalCancelEconomicGroup from "../../insurance_company/components/ModalCancelInsurance";
const STEPS_GARANTIA = [
  {
    name: "1. Grupo Econômico",
    step: 1
  },
  {
    name: "2. Adicionar tomador matriz",
    step: 2
  }
];

const STEPS = [
  {
    name: "1. Grupo Econômico",
    step: 1
  },
  {
    name: "2. Adicionar matriz",
    step: 2
  }
];

const defaultFilterProps = {
  outlined: true,
  dense: true,
  "background-color": "white",
  class: "filter__icon",
  "append-outer-icon": "fa-search"
};

const stepModelName = { group: "groupInfo", tomador: "companyInfo" };

const groupInfoKeys = [
  "exibir_comissao_cotacao",
  "representado_por_pj",
  "cnpj",
  "nome",
  "razao_social",
  "escritorios",
  "contatos"
];

export default {
  beforeRouteLeave(to, from, next) {
    if (this.cancel || this.formIsPristine()) next();
    this.modalCancel = true;
    this.nextRoute = to;
  },
  mixins: [LoadingRequest, EconomicGroupMixin, CompanyMixin],
  components: {
    StepToolbar,
    NewEconomicGroupForm,
    NewCompanyForm,
    BalanceModal,
    SingleDocumentModal,
    NewContactModal,
    FormSection,
    CompanyDetailsFormSection,
    ModalNewEconomicGroup,
    ModalCancelEconomicGroup
  },
  data: () => ({
    step: "group",
    isValidForm: false,
    isBalanceModalOpen: false,
    isContactModalOpen: false,
    contact: {},
    advisorErrorMessages: [],
    groupInfo: {
      exibir_comissao_cotacao: true,
      representado_por_pj: false,
      contatos: [],
      atendentes: null
    },
    cloneForm: {
      exibir_comissao_cotacao: true,
      representado_por_pj: false,
      contatos: [],
      atendentes: null
    },
    balanceDocuments: { documents: [] },
    otherDocuments: {
      contrato_social: [],
      estatuto: [],
      ata: [],
      outros: []
    },
    cloneBalanceDocuments: { documents: [] },
    cloneOtherDocuments: {
      contrato_social: [],
      estatuto: [],
      ata: [],
      outros: []
    },
    defaultFilterProps,
    companyInfo: { endereco: {}, atividade_principal: {} },
    companyBalanceDocuments: { documents: [] },
    companyOtherDocuments: {
      contrato_social: [],
      estatuto: [],
      ata: [],
      outros: []
    },
    documentModalForKey: null,
    createdEconomicGroup: null,
    atendentList: [],
    comercialList: [],
    productList: [],
    contactsErrorMessages: [],
    breadcrumb: [
      {
        text: "Adicionar Grupo Econômico",
        to: "economic_group"
      }
    ],
    modalNewItem: false,
    newEconomicGroup: null,
    oldCNAE: null,
    modalCancel: false,
    cancel: false,
    nextRoute: null,
    officeList: []
  }),
  created() {
    const payload = "";
    const payload_user_comercial =
      "?&estrutura_id=funcionario&posicao_id=comercial";
    const payload_user_atendent =
      "?&estrutura_id=funcionario&posicao_id=tecnico";
    let vm = this;
    this.withLoading(
      Promise.all([
        this.suggestOperationalGroups(payload).then(users => {
          vm.productList = users;
        }),
        this.suggestUsers(payload_user_comercial).then(users => {
          vm.comercialList = users;
        }),
        this.suggestUsers(payload_user_atendent).then(users => {
          vm.atendentList = users;
        }),
        this.requestOffices().then(list => {
          vm.officeList = list;
        })
      ])
    );
  },
  computed: {
    ...mapState("me", ["myInfo", "userAreaIsGarantia"]),
    userArea() {
      return this.myInfo.area_usuario;
    },
    isDocumentsRequired() {
      return this.userAreaIsGarantia;
    },
    toolbarSteps() {
      return this.userAreaIsGarantia ? STEPS_GARANTIA : STEPS;
    },
    textMatriz() {
      return this.userAreaIsGarantia ? "Tomador Matriz" : "Matriz";
    },
    textMatrizFilial() {
      return this.userAreaIsGarantia
        ? "Tomador Matriz/Filial"
        : "Matriz/Filial";
    },
    textNotification() {
      return this.userAreaIsGarantia
        ? "Você deseja criar um novo tomador matriz ou filial?"
        : "Você deseja criar uma nova matriz ou filial?";
    },
    askForCreateCompany() {
      return Boolean(this.createdEconomicGroup);
    },
    currentStep() {
      return { step: this.step === "group" ? 1 : 2 };
    },
    selectBalanceDocumentModel() {
      return this.step === "group"
        ? this.balanceDocuments
        : this.companyBalanceDocuments;
    },
    selectDocumentModel() {
      return this.step === "group"
        ? this.otherDocuments
        : this.companyOtherDocuments;
    },
    cloneModel() {
      const model = this[stepModelName[this.step]];
      return {
        ...model,
        atividade_principal: {
          ...model.atividade_principal
        },
        endereco: {
          ...model.endereco
        }
      };
    },
    formData() {
      const { cnpj, razao_social, ...group } = this.groupInfo;

      const representedByCompany = ({ representado_por_pj }) =>
        !representado_por_pj
          ? null
          : {
              cnpj,
              razao_social
            };

      return {
        ...group,
        ...representedByCompany(this.groupInfo),
        balancos: [],
        documentos: []
      };
    }
  },
  methods: {
    ...mapActions([
      "suggestUsers",
      "showAlertNotification",
      "suggestOperationalGroups"
    ]),
    ...mapActions("economic_group", [
      "findLegalPerson",
      "findCEP",
      "findCNAE",
      "createEconomicGroup",
      "createCompany",
      "requestOffices",
      "requestUploadLogo"
    ]),
    closeModalCancel(value) {
      this.cancel = value;
      this.modalCancel = false;
      if (value) {
        this.$router.push({
          name: this.nextRoute.name,
          params: this.nextRoute.params
        });
      }
    },
    formIsPristine() {
      return (
        _.isEqual(_.omit(this.cloneForm), _.omit(this.groupInfo)) &&
        _.isEqual(this.cloneBalanceDocuments, this.balanceDocuments) &&
        _.isEqual(this.cloneOtherDocuments, this.otherDocuments)
      );
    },
    updateModel(newModel) {
      const modelKey = stepModelName[this.step];

      if (this.step === "group") {
        // NOTE: apenas para grupo precisamos
        // reduzir o objeto para os itens necessários.
        newModel = _.pick(newModel, groupInfoKeys);
      }

      this[modelKey] = newModel;
    },
    onSearchUser(payload, list) {
      let vm = this;
      if (list == "productList") {
        this.withLoading(
          this.suggestOperationalGroups(payload).then(users => {
            vm[list] = users;
          })
        );
      } else {
        this.withLoading(
          this.suggestUsers(payload).then(users => {
            vm[list] = users;
          })
        );
      }
    },
    onAddContact(item) {
      const guid = objectGuid(item);
      if (!guid) {
        this.groupInfo.contatos = this.groupInfo.contatos.concat([
          defineGuid(item)
        ]);
      }
      this.contact = {};
      this.isContactModalOpen = false;
    },
    onEditContact(item) {
      this.contact = this.groupInfo.contatos.find(
        contact => objectGuid(contact) === objectGuid(item)
      );
      this.isContactModalOpen = true;
    },
    onRequestedMoreAdvisories() {
      this.groupInfo.assessores = this.groupInfo.assessores.concat([
        { nome: "" }
      ]);
    },
    validate() {
      this.balanceFormErrors = [];
      this.isValidForm = true;

      if (this.step != "group") {
        return true;
      } else {
        this.balanceFormErrors = [];
        if (
          this.isDocumentsRequired &&
          this.balanceDocuments.documents.length == 0
        ) {
          this.balanceFormErrors = [
            "É preciso adicionar pelo menos um arquivo de balanço."
          ];
          this.isValidForm = false;
        }

        this.contactsErrorMessages = [];
        if (this.groupInfo.contatos.length == 0) {
          this.contactsErrorMessages = [
            "É preciso adicionar pelo menos um contato."
          ];
          this.isValidForm = false;
        }
      }

      return this.$refs.form.validate() && this.isValidForm;
    },
    async submitEconomicGroup() {
      /* Se tiver logo, preciso dar upload na logo
        e adicionar a url antes de criar novo grupo econômico*/
      if (this.formData.logo) {
        try {
          const logo = await this.withLoading(
            this.requestUploadLogo(
              this.getUploadFilePayload(this.formData.logo)
            )
          );
          this.formData.logo = logo;
        } catch (error) {
          console.error("Error uploading logo:", error);
        }
      }

      return this.submitDocuments(
        this.balanceDocuments,
        this.otherDocuments
      ).then(({ balancos, documentos }) => {
        let payload = _.cloneDeep(this.formData);
        let userArea = this.userArea;
        if (payload.cnpj) payload.cnpj = normalizeCnpj(payload.cnpj);
        return this.createEconomicGroup({
          ...payload,
          documentos,
          balancos,
          userArea
        }).catch(error => {
          this.showAlertNotification({
            message: !error.response
              ? "Aconteceu um erro ao tentar salvar o Grupo Econômico"
              : error.response.data.details &&
                error.response.data.details.length > 0
              ? error.response.data.details.reduce(
                  (acc, { message }) => acc.concat(message),
                  ""
                )
              : error.response.data.error_description,
            type: "error"
          });
        });
      });
    },
    submitCompany(economicGroup) {
      return this.submitDocuments(
        this.companyBalanceDocuments,
        this.companyOtherDocuments
      ).then(({ balancos, documentos }) => {
        let payload = _.cloneDeep(this.companyInfo);
        payload.cnpj = normalizeCnpj(payload.cnpj);
        this.withLoading(
          this.createCompany({
            groupId: economicGroup.id,
            info: {
              ...payload,
              documentos,
              balancos
            }
          }).then(() => {
            this.showAlertNotification({
              message: "Cadastrado com sucesso!",
              type: "success"
            });
            this.cancel = true;
            this.$router.push({ name: "economic_group list" });
          })
        ).catch(error => {
          this.showAlertNotification({
            message: !error.response
              ? "Aconteceu um erro ao tentar salvar o Grupo Econômico"
              : error.response.data.details &&
                error.response.data.details.length > 0
              ? error.response.data.details.reduce(
                  (acc, { message }) => acc.concat(message),
                  ""
                )
              : error.response.data.error_description,
            type: "error"
          });
        });
      });
    },
    onRemoveEconomicGroupOthersDocuments(item) {
      this.otherDocuments[item.tipo_documento.id] = this.otherDocuments[
        item.tipo_documento.id
      ].filter(document => document.arquivo.nome !== item.arquivo.nome);
    },
    onRemoveCompanyOthersDocuments(item) {
      this.companyOtherDocuments[
        item.tipo_documento.id
      ] = this.companyOtherDocuments[item.tipo_documento.id].filter(
        document => document.arquivo.nome !== item.arquivo.nome
      );
    },
    previousPage() {
      if (this.step == "group") {
        this.$router.push({ name: "economic_group" });
      } else {
        this.step = "group";
      }
    },
    submit() {
      if (!this.validate()) {
        return;
      }
      if (this.step === "group") {
        this.withLoading(
          this.submitEconomicGroup().then(economicGroup => {
            this.newEconomicGroup = economicGroup;
            this.modalNewItem = true;
          })
        );
      } else {
        this.withLoading(this.submitCompany(this.newEconomicGroup));
      }
    },
    closeModalNewItem() {
      this.modalNewItem = false;
      this.cancel = true;
      this.$router.push({ name: "economic_group list" });
    },
    newTaker() {
      this.modalNewItem = false;
      this.step = "tomador";
    }
  },
  watch: {
    "groupInfo.cnpj"(value) {
      const cnpj = normalizeCnpj(value);
      if (BRUtils.isValidCNPJ(cnpj)) {
        this.findLegalPersonProxy(cnpj);
      }
    },
    "companyInfo.cnpj"(value) {
      const cnpj = normalizeCnpj(value);
      if (BRUtils.isValidCNPJ(cnpj)) {
        this.findLegalPersonProxy(cnpj);
      }
    },
    "companyInfo.atividade_principal.codigo"(value) {
      const cnae = value.replace(/\D/g, "");
      // Evitar de ficar num looping infinito
      if (cnae.length == 7 && this.oldCNAE != cnae) {
        this.findCNAEProxy(cnae);
      }
    }
  }
};
</script>

<style lang="sass">
.new-econo-group__modal
  background: #fff !important
  border-radius: 2px !important
  h3
    text-transform: uppercase

.dialog__close-btn
  font-size: 18px !important
</style>
